// Mapbox Search Box API service with type-safe parameter handling and structured logging

import { searchCache, reverseCache, detailsCache } from '../utils/mapboxCache';

const MAPBOX_BASE_URL = 'https://api.mapbox.com/search/searchbox/v1';
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Valid parameters for each endpoint
const ENDPOINT_PARAMS = {
  suggest: {
    required: ['access_token', 'q', 'session_token'],
    optional: ['language', 'limit', 'proximity', 'country', 'types', 'bbox']
  },
  retrieve: {
    required: ['access_token', 'session_token'],
    optional: []
  },
  reverse: {
    required: ['access_token', 'longitude', 'latitude'],
    optional: ['language', 'limit', 'country', 'types']
  }
};

// Abort controller utilities
export function createAbortController(reason) {
  const controller = new AbortController();
  controller._abortReason = reason;
  return controller;
}

export function abortWithReason(controller, reason) {
  if (controller && !controller.signal.aborted) {
    controller._abortReason = reason;
    controller.abort();
  }
}

// Generate correlation ID for request tracking
function generateCorrelationId() {
  return `mbx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Format address object to string
function formatAddress(address) {
  if (!address) return '';
  if (typeof address === 'string') return address;
  
  if (typeof address === 'object') {
    // Handle Mapbox address object structure
    const parts = [
      address.street,
      address.place,
      address.postcode,
      address.country
    ].filter(Boolean); // Remove null/undefined values
    
    if (parts.length > 0) {
      return parts.join(', ');
    }
    
    // Fallback: try other possible structures
    if (address.formatted) return address.formatted;
    if (address.full) return address.full;
    
    // Last resort: stringify the object for debugging
    console.warn('[MapboxAPI] Unexpected address structure:', address);
    return JSON.stringify(address);
  }
  
  return String(address);
}

// Structured logging helper
function logRequest(endpoint, params, startTime, response = null, error = null, correlationId = null) {
  // Only log detailed info in development
  if (process.env.NODE_ENV !== 'development') {
    // In production, only log errors
    if (error && error.name !== 'AbortError') {
      console.error(`[MapboxAPI Error] ${endpoint}: ${error.message}`);
    }
    return;
  }
  
  const logData = {
    timestamp: new Date().toISOString(),
    correlationId,
    endpoint,
    url: `${MAPBOX_BASE_URL}/${endpoint}`,
    params: { ...params, access_token: params.access_token ? '[REDACTED]' : undefined },
    duration_ms: Math.round(performance.now() - startTime),
    status: response?.status || null,
    error: error?.message || null,
    errorType: error?.name || null,
    abortReason: error?.name === 'AbortError' ? (error.cause || 'No reason provided') : null
  };
  
  // Different log levels based on error type
  if (error?.name === 'AbortError') {
    // Aborted requests are expected behavior, log as info
    console.info('[MapboxAPI Aborted]', JSON.stringify(logData));
  } else if (error || (response && !response.ok)) {
    // Real errors
    console.error('[MapboxAPI Error]', JSON.stringify(logData));
  } else {
    // Successful requests
    console.info('[MapboxAPI Success]', JSON.stringify(logData));
  }
  
  return logData;
}

// Extract base endpoint name from parameterized paths (e.g., "retrieve/123" -> "retrieve")
function getBaseEndpoint(endpoint) {
  // Handle parameterized endpoints by extracting the base path
  const firstSlashIndex = endpoint.indexOf('/');
  if (firstSlashIndex !== -1) {
    return endpoint.substring(0, firstSlashIndex);
  }
  return endpoint;
}

// Validate parameters against endpoint requirements
function validateParams(endpoint, params) {
  // Extract base endpoint for validation (e.g., "retrieve/123" -> "retrieve")
  const baseEndpoint = getBaseEndpoint(endpoint);
  const config = ENDPOINT_PARAMS[baseEndpoint];
  
  if (!config) {
    throw new Error(`Unknown endpoint: ${endpoint} (base: ${baseEndpoint})`);
  }
  
  // Check required parameters
  for (const required of config.required) {
    if (!params[required]) {
      throw new Error(`Missing required parameter for ${endpoint}: ${required}`);
    }
  }
  
  // Filter out invalid parameters
  const validParams = {};
  const allValidParams = [...config.required, ...config.optional];
  
  for (const [key, value] of Object.entries(params)) {
    if (allValidParams.includes(key) && value !== undefined && value !== null) {
      validParams[key] = value;
    } else if (!allValidParams.includes(key)) {
      console.warn(`[MapboxAPI] Ignoring invalid parameter '${key}' for endpoint '${endpoint}'`);
    }
  }
  
  return validParams;
}

// Generic fetch wrapper with logging and error handling
async function mapboxFetch(endpoint, params, options = {}) {
  const startTime = performance.now();
  const correlationId = generateCorrelationId();
  let response;
  let responseData;
  
  try {
    // Validate and filter parameters
    const validParams = validateParams(endpoint, { access_token: MAPBOX_TOKEN, ...params });
    
    // Log request start (only in development)
    if (process.env.NODE_ENV === 'development') {
      console.info('[MapboxAPI Start]', JSON.stringify({
        timestamp: new Date().toISOString(),
        correlationId,
        endpoint,
        url: `${MAPBOX_BASE_URL}/${endpoint}`,
        params: { ...validParams, access_token: '[REDACTED]' }
      }));
    }
    
    // Build URL
    const url = new URL(`${MAPBOX_BASE_URL}/${endpoint}`);
    Object.entries(validParams).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
    
    // Make request
    response = await fetch(url.toString(), {
      signal: options.abortSignal,
      ...options
    });
    
    // Handle non-OK responses
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      const errorMessage = errorBody || response.statusText || `HTTP ${response.status}`;
      throw new Error(`Mapbox API error: ${errorMessage}`);
    }
    
    // Parse response
    responseData = await response.json();
    
    // Log successful request
    logRequest(endpoint, validParams, startTime, response, null, correlationId);
    
    return responseData;
    
  } catch (error) {
    // Extract abort reason if available
    if (error.name === 'AbortError' && options.abortSignal?._abortReason) {
      error.cause = options.abortSignal._abortReason;
    }
    
    // Log error
    logRequest(endpoint, params, startTime, response, error, correlationId);
    
    // Re-throw with context
    if (error.name === 'AbortError') {
      throw error;
    }
    throw new Error(`Failed to ${endpoint}: ${error.message}`);
  }
}

// Endpoint-specific functions

export async function searchSuggest(query, options = {}) {
  const params = {
    q: query,
    session_token: options.sessionToken,
    limit: options.limit || '6',
    language: options.language,
    proximity: options.proximity,
    country: options.country,
    types: options.types
  };
  
  // Check cache first
  const cached = searchCache.get('suggest', params);
  if (cached) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[MapboxAPI Cache Hit] suggest:', { query, proximity: params.proximity });
    }
    return cached;
  }
  
  // Make API request if not cached
  const data = await mapboxFetch('suggest', params, {
    abortSignal: options.abortSignal
  });
  
  const results = data.suggestions || [];
  
  // Cache successful results
  if (results.length > 0) {
    searchCache.set('suggest', params, results);
  }
  
  return results;
}

export async function retrieveDetails(suggestionId, sessionToken, options = {}) {
  // Check cache first
  const cacheParams = { suggestionId };
  const cached = detailsCache.get('retrieve', cacheParams);
  if (cached) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[MapboxAPI Cache Hit] retrieve:', { suggestionId });
    }
    return cached;
  }
  
  const endpoint = `retrieve/${suggestionId}`;
  const params = {
    session_token: sessionToken
  };
  
  const data = await mapboxFetch(endpoint, params, {
    abortSignal: options.abortSignal
  });
  
  if (!data.features || data.features.length === 0) {
    throw new Error('No location details found');
  }
  
  const feature = data.features[0];
  
  // Format address if it's an object
  const formattedAddress = formatAddress(feature.properties.address);
  
  const result = {
    name: feature.properties.name || formattedAddress || feature.properties.full_address || '',
    lat: feature.geometry.coordinates[1],
    lng: feature.geometry.coordinates[0],
    placeId: suggestionId,
    source: 'mapbox',
    context: feature.properties.place_formatted || feature.properties.full_address || ''
  };
  
  // Cache the result
  detailsCache.set('retrieve', cacheParams, result);
  
  return result;
}

export async function reverseGeocode(longitude, latitude, options = {}) {
  // Validate coordinates
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    throw new Error(`Invalid coordinates: lat=${latitude}, lng=${longitude}`);
  }
  
  const params = {
    longitude: longitude.toString(),
    latitude: latitude.toString(),
    limit: options.limit || '5',
    language: options.language,
    country: options.country,
    types: options.types
  };
  
  // Check cache first
  const cached = reverseCache.get('reverse', params);
  if (cached) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[MapboxAPI Cache Hit] reverse:', { longitude, latitude });
    }
    return cached;
  }
  
  const data = await mapboxFetch('reverse', params, {
    abortSignal: options.abortSignal
  });
  
  const results = [];
  
  // Always add current location as first option
  results.push({
    name: 'Current Location',
    lat: latitude,
    lng: longitude,
    source: 'manual',
    context: 'Your current location'
  });
  
  // Add nearby places
  if (data.features) {
    data.features.forEach((feature) => {
      // Format address if it's an object
      const formattedAddress = formatAddress(feature.properties.address);
      
      results.push({
        name: feature.properties.name || formattedAddress || feature.properties.full_address || '',
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
        placeId: feature.properties.mapbox_id,
        source: 'mapbox',
        context: feature.properties.place_formatted || feature.properties.full_address || ''
      });
    });
  }
  
  // Cache the results
  reverseCache.set('reverse', params, results);
  
  return results;
}

// Export utility to check if token is configured
export function isMapboxConfigured() {
  return Boolean(MAPBOX_TOKEN);
}